import {
  apiClient,
  Ticket,
  Message,
  PaginatedResponse,
  CreateTicketRequest,
} from './api';

export const ticketService = {
  // Get customer's tickets
  async getMyTickets(options?: {
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<Ticket>> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());

    const response = await apiClient.get<PaginatedResponse<Ticket>>(
      `/tickets/my-tickets?${params.toString()}`
    );
    return response.data;
  },

  // Get single ticket details
  async getTicket(id: string): Promise<Ticket> {
    const response = await apiClient.get<Ticket>(`/tickets/my-tickets/${id}`);
    return response.data;
  },

  // Get ticket messages/conversation
  async getTicketMessages(ticketId: string): Promise<Message[]> {
    const response = await apiClient.get<Message[]>(
      `/tickets/my-tickets/${ticketId}/messages`
    );
    return response.data;
  },

  // Create a new ticket
  async createTicket(data: CreateTicketRequest): Promise<Ticket> {
    const response = await apiClient.post<Ticket>('/tickets', data);
    return response.data;
  },

  // Reply to a ticket
  async replyToTicket(
    ticketId: string,
    content: string,
    attachments?: File[]
  ): Promise<Message> {
    const formData = new FormData();
    formData.append('content', content);

    if (attachments) {
      attachments.forEach((file) => {
        formData.append('attachments', file);
      });
    }

    const response = await apiClient.post<Message>(
      `/tickets/my-tickets/${ticketId}/reply`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Close a ticket (customer initiated)
  async closeTicket(ticketId: string): Promise<Ticket> {
    const response = await apiClient.post<Ticket>(
      `/tickets/my-tickets/${ticketId}/close`
    );
    return response.data;
  },

  // Reopen a ticket
  async reopenTicket(ticketId: string, reason?: string): Promise<Ticket> {
    const response = await apiClient.post<Ticket>(
      `/tickets/my-tickets/${ticketId}/reopen`,
      { reason }
    );
    return response.data;
  },

  // Upload attachment
  async uploadAttachment(ticketId: string, file: File): Promise<{ url: string; filename: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<{ url: string; filename: string }>(
      `/tickets/my-tickets/${ticketId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },
};
